import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { CompanyStatusBadge } from '../components/StatusBadge.tsx';
import type { CompanyStatus } from '../lib/types.ts';

type ExpiringRow = {
  id: string;
  file_name: string;
  file_path: string;
  expires_at: string;
  created_at: string;
  option_key: string | null;
  submission: {
    id: string;
    question_id: string;
    company_id: string;
    company: {
      id: string;
      company_name: string;
      contact_name: string;
      email: string;
      status: CompanyStatus;
    } | null;
    question: { id: string; label: string } | null;
  } | null;
};

type ExpiringResponse = {
  expired: ExpiringRow[];
  expiring: ExpiringRow[];
  horizon_days: number;
};

export function AdminExpiringDocuments() {
  const { data, isLoading } = useQuery({
    queryKey: ['expiring-documents'],
    queryFn: () => adminApi.get<ExpiringResponse>('/api/companies/files/expiring?days=30'),
  });

  if (isLoading) return <CenteredSpinner />;

  const expired = data?.expired ?? [];
  const expiring = data?.expiring ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Expiring documents</h1>
        <p className="mt-1 text-sm text-slate-500">
          Uploaded documents that have expired or are due to expire within{' '}
          {data?.horizon_days ?? 30} days. Companies with expired documents are
          flagged non-compliant.
        </p>
      </div>

      <Section title="Expired" rows={expired} emptyHint="No expired documents." tone="danger" />
      <Section
        title="Expiring soon"
        rows={expiring}
        emptyHint="No upcoming expiries in the next 30 days."
        tone="warn"
      />
    </div>
  );
}

function Section({
  title,
  rows,
  emptyHint,
  tone,
}: {
  title: string;
  rows: ExpiringRow[];
  emptyHint: string;
  tone: 'danger' | 'warn';
}) {
  if (rows.length === 0) {
    return (
      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          {title}
        </h2>
        <EmptyState title={title} description={emptyHint} />
      </section>
    );
  }

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        {title} <span className="text-slate-400">({rows.length})</span>
      </h2>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Document</th>
              <th className="px-4 py-2">Company</th>
              <th className="px-4 py-2">Question</th>
              <th className="px-4 py-2">Expires</th>
              <th className="px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => {
              const expiry = new Date(row.expires_at);
              const company = row.submission?.company;
              return (
                <tr key={row.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-900">
                    {row.file_name}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {company ? (
                      <Link
                        to={`/admin/companies/${company.id}`}
                        className="hover:underline"
                      >
                        {company.company_name}
                      </Link>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                    {company && (
                      <div className="text-xs text-slate-500">{company.email}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {row.submission?.question?.label ?? '—'}
                  </td>
                  <td
                    className={`px-4 py-2 ${
                      tone === 'danger' ? 'text-red-700' : 'text-amber-700'
                    }`}
                  >
                    {expiry.toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">
                    {company ? <CompanyStatusBadge status={company.status} /> : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
